import { useEffect, useState } from 'react';
import { App, Button, Card, Col, Form, Input, Row, Space, Tag, Typography } from 'antd';
import type { AuthUser } from '@crm/shared';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/PageHeader';
import { api, errorMessage } from '../api/client';
import { CardTitle } from '../components/StatCard';
import { PhotoAvatar } from '../components/PhotoAvatar';

const initials = (s: string) =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

export function AccountPage() {
  const { user, updateUser } = useAuth();
  const { message } = App.useApp();
  const [profileForm] = Form.useForm<{ name: string }>();
  const [form] = Form.useForm();
  const [savingProfile, setSavingProfile] = useState(false);
  const [saving, setSaving] = useState(false);

  // the form is created before the session is known on a hard reload
  useEffect(() => {
    profileForm.setFieldsValue({ name: user?.name ?? '' });
  }, [profileForm, user?.name]);

  const onSaveProfile = async (values: { name: string }) => {
    setSavingProfile(true);
    try {
      const r = await api.patch<AuthUser>('/auth/me', values);
      updateUser({ name: r.data.name });
      message.success('Profile updated');
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const onFinish = async (values: { currentPassword: string; newPassword: string }) => {
    setSaving(true);
    try {
      await api.post('/auth/change-password', values);
      message.success('Password changed');
      form.resetFields();
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page page-narrow">
      <PageHeader title="My account" subtitle="Your profile and password" />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <Card title={<CardTitle title="Profile" subtitle="How you appear to your team" />}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
            {user && (
              <PhotoAvatar kind="user" recordId={user.id} fileId={user.photoFileId} fallback={initials(user.name)} size={80} onChanged={(photoFileId) => updateUser({ photoFileId })} />
            )}
            <Form form={profileForm} layout="vertical" onFinish={onSaveProfile} requiredMark={false} initialValues={{ name: user?.name ?? '' }} style={{ flex: 1, minWidth: 260 }}>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Enter your name' }, { max: 120 }]}>
                    <Input autoComplete="name" placeholder="Your name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Email">
                    <Input value={user?.email ?? ''} disabled />
                  </Form.Item>
                </Col>
              </Row>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Space size={8}>
                  <Tag color={user?.role === 'SUPER_ADMIN' ? 'processing' : 'default'}>{user?.role === 'SUPER_ADMIN' ? 'Super admin' : 'Admin'}</Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    Click the photo to upload or remove it.
                  </Typography.Text>
                </Space>
                <Button type="primary" htmlType="submit" loading={savingProfile}>
                  Save profile
                </Button>
              </div>
            </Form>
          </div>
        </Card>
        <Card title={<CardTitle title="Change password" subtitle="Use at least 10 characters" />}>
          <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item name="currentPassword" label="Current password" rules={[{ required: true }]}>
                  <Input.Password autoComplete="current-password" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="newPassword" label="New password" rules={[{ required: true, min: 10, message: 'At least 10 characters' }]}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" loading={saving}>
                Update password
              </Button>
            </div>
          </Form>
        </Card>
      </Space>
    </div>
  );
}
